import type { EstimateDto, EstimateLineDto } from "@/shared/api/types";
import type { BillingPeriod, CatalogueItemDto, PlanItemSelection } from "@/shared/api/types";
import { peoplePerUnit } from "@/shared/utils/capacity";
import { formatCount } from "@/shared/utils/numbers";

/**
 * The arithmetic and wording behind the plan configurator (ADR-0015 §1–§2, M11 §1). Everything here is
 * pure and unit-tested: the api prices an order, this file only decides what the customer is shown
 * before one exists. Amounts are in PAISE throughout — the money never leaves minor units until
 * `formatMoney` puts it on screen.
 */

export interface PlanConfiguration {
  period: BillingPeriod;
  items: PlanItemSelection[];
}

/** What the configurator is being used for; it decides the caps, the wording and what a change costs. */
export type ConfiguratorMode = "signup" | "subscribe" | "change";

export function quantityOf(items: readonly PlanItemSelection[], code: string): number {
  return items.find((i) => i.code === code)?.quantity ?? 0;
}

/** A new configuration with one item's quantity replaced; row order (the catalogue's) is preserved. */
export function withQuantity(
  config: PlanConfiguration,
  code: PlanItemSelection["code"],
  quantity: number,
): PlanConfiguration {
  const present = config.items.some((i) => i.code === code);
  return {
    period: config.period,
    items: present
      ? config.items.map((i) => (i.code === code ? { ...i, quantity } : i))
      : [...config.items, { code, quantity }],
  };
}

/**
 * The rows worth sending: the estimate refuses an empty list and prices a zero line at zero anyway, and
 * `POST /tenants` would otherwise open a trial on an add-on nobody picked.
 */
export function nonZeroItems(config: PlanConfiguration): PlanItemSelection[] {
  return config.items.filter((i) => i.quantity > 0);
}

/**
 * A configuration built from quantities the api reported, keyed by item code. The catalogue drives it, so
 * the codes are the typed ones and an item the organisation holds but nobody sells any more (a retired row)
 * simply drops out of the next request rather than being re-sent as a quantity.
 */
export function selectionFromQuantities(
  catalogue: readonly CatalogueItemDto[],
  quantities: Readonly<Record<string, number>>,
): PlanItemSelection[] {
  return sellable(catalogue).map((item) => ({ code: item.code, quantity: quantities[item.code] ?? 0 }));
}

/** What the organisation holds today: quantities of the items it is actually running (bought or trialling). */
export function heldQuantities(
  items: Readonly<Record<string, { state: string; quantity: number }>>,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [code, item] of Object.entries(items)) {
    if (item.state === "active" || item.state === "trial") out[code] = item.quantity;
  }
  return out;
}

/**
 * The configuration the api would apply by default: the base item at its minimum, no add-ons, monthly
 * (ADR-0015 §4). An add-on's `minQuantity` is how few may be *held* — the flat ones are 1, one per
 * organisation — not a default; starting every add-on ticked would price a signup at things nobody chose.
 */
export function defaultConfiguration(catalogue: readonly CatalogueItemDto[]): PlanConfiguration {
  return {
    period: "monthly",
    items: sellable(catalogue).map((item) => ({
      code: item.code,
      quantity: item.kind === "base" ? Math.max(item.minQuantity, 1) : 0,
    })),
  };
}

/** Only `active` rows are on sale; `draft` is a module being built and `retired` is one nobody may buy. */
export function sellable(catalogue: readonly CatalogueItemDto[]): CatalogueItemDto[] {
  return [...catalogue].filter((i) => i.status === "active").sort((a, b) => a.sort - b.sort);
}

export function baseItem(catalogue: readonly CatalogueItemDto[]): CatalogueItemDto | null {
  return sellable(catalogue).find((i) => i.kind === "base") ?? null;
}

/** One unit's price for a whole period, or null while the item has no price version yet. */
export function unitPaise(item: CatalogueItemDto, period: BillingPeriod): number | null {
  if (!item.price) return null;
  return period === "yearly" ? item.price.yearlyPaise : item.price.monthlyPaise;
}

/**
 * What one unit costs a month, whichever period is chosen — the only comparison that means anything to a
 * customer ("₹99 / terminal / month, billed yearly" beside "₹129 / month", ADR-0015 D2).
 */
export function unitPerMonthPaise(item: CatalogueItemDto, period: BillingPeriod): number | null {
  const paise = unitPaise(item, period);
  if (paise === null) return null;
  return period === "yearly" ? Math.round(paise / 12) : paise;
}

/**
 * How much cheaper the yearly price is, from the catalogue rather than a hard-coded 20 %: prices are
 * edited from the internal portal and the badge has to follow them. Read off the base plan, because it
 * is the line every organisation buys; null when it has no price or yearly is not actually cheaper.
 */
export function yearlySavingPercent(catalogue: readonly CatalogueItemDto[]): number | null {
  const price = baseItem(catalogue)?.price;
  if (!price || price.monthlyPaise <= 0) return null;
  const saving = Math.round((1 - price.yearlyPaise / (price.monthlyPaise * 12)) * 100);
  return saving > 0 ? saving : null;
}

/**
 * The terminals a configuration holds: the quantity of EVERY item whose unit is a terminal — Access Control
 * and, since M23, Extra terminal — exactly as the api's `limitsFor` counts `devices`. "Terminals = the base
 * plan's quantity" was true while the base was the only terminal on sale; read that way, 1 Access Control +
 * 2 Extra terminals with 3 paired would be told it is too small for what it already runs.
 */
export function terminalCount(catalogue: readonly CatalogueItemDto[], config: PlanConfiguration): number {
  return catalogue
    .filter((item) => item.unitKind === "terminal")
    .reduce((total, item) => total + quantityOf(config.items, item.code), 0);
}

/** One line of the people arithmetic: `quantity` units of `item`, each bringing `each` people. */
export interface PeopleSource {
  item: CatalogueItemDto;
  quantity: number;
  each: number;
}

/**
 * Where the people a configuration holds come from, in catalogue order. `peopleAllowance` is their sum and
 * the Staff row's sentence is their words, so the number and the explanation of it can never disagree.
 */
export function peopleSources(
  catalogue: readonly CatalogueItemDto[],
  config: PlanConfiguration,
): PeopleSource[] {
  return sellable(catalogue).flatMap((item) => {
    const quantity = quantityOf(config.items, item.code);
    const each = peoplePerUnit(item);
    return quantity > 0 && each > 0 ? [{ item, quantity, each }] : [];
  });
}

/** People included by a configuration: the sum of its `peopleSources` (`peoplePerUnit`, `utils/capacity`). */
export function peopleAllowance(catalogue: readonly CatalogueItemDto[], config: PlanConfiguration): number {
  return peopleSources(catalogue, config).reduce((total, source) => total + source.quantity * source.each, 0);
}

/**
 * The range a stepper may move in: the catalogue's own min/max, capped by the trial maximum at signup — and,
 * for an add-on sold per employee, by `ceiling`, the people the configuration holds (ADR-0015 addendum of
 * 2026-10-01, rule 2: a staff quantity above people capacity is refused, so the stepper never offers one).
 */
export function quantityBounds(
  item: CatalogueItemDto,
  mode: ConfiguratorMode,
  ceiling?: number,
): { min: number; max: number } {
  const catalogueMax = item.maxQuantity ?? 9999;
  const trialMax = mode === "signup" ? Math.min(catalogueMax, item.trialMaxQuantity) : catalogueMax;
  const max = ceiling === undefined ? trialMax : Math.min(trialMax, ceiling);
  // The floor is the base item's minimum — an organisation always has at least one terminal — but 0 for
  // every add-on, whatever the catalogue says: 0 is how an add-on is dropped, and its `minQuantity`
  // describes how few may be held once it is held at all (the flat ones are one per organisation).
  const min = item.kind === "base" ? item.minQuantity : 0;
  return { min, max: Math.max(max, min) };
}

/**
 * "2 terminals", "2 × lot of 250 people". A unit label is whatever the internal portal typed into the
 * catalogue, and some of them are phrases — naive pluralisation turns "lot of 250 people" into
 * "lot of 250 peoples", which is how a price list loses a customer's trust before the price does.
 */
export function unitCount(quantity: number, unitLabel: string): string {
  // Grouped either way: "1,250 employees" beside "1,250 people", never "1250" in one and "1,250" in the other.
  return /\s/.test(unitLabel)
    ? `${formatCount(quantity)} × ${unitLabel}`
    : `${formatCount(quantity)} ${quantity === 1 ? unitLabel : `${unitLabel}s`}`;
}

/**
 * What to call an item in a sentence ABOUT A CHANGE: its catalogue name, the base plan included.
 *
 * M11 §14 named the base plan after what it counted ("Terminals: 3 → 2"), because "Access Control: 3 → 2"
 * read as the product being halved — true while Access Control was the only terminal. Since M23 a reshape
 * puts "Terminals: 3 → 1" beside "Extra terminal: 0 → 2", which says the terminals are going while they are
 * not; the review names each line as the "What you will have" card above it does, and the card's totals
 * ("3 terminals · up to 250 people") are what say what is being counted.
 */
export function itemChangeName(item: CatalogueItemDto): string {
  return item.name;
}

/** An add-on bought one-per-organisation (API access, Webhooks) is a tick, not a stepper. */
export function isFlat(item: CatalogueItemDto): boolean {
  return item.unitKind === "flat";
}

/**
 * An add-on sold per employee (Staff). A stepper like the terminals', with two differences the rest of this
 * folder keeps asking about: it can never exceed the people the configuration holds, and what it costs is
 * per employee, not per organisation.
 */
export function isPerEmployee(item: CatalogueItemDto): boolean {
  return item.unitKind === "employee";
}

export interface UnitPricePresentation {
  /** What one unit costs a month at the chosen period, after any discount the estimate applied. */
  perMonthPaise: number | null;
  /** The price to strike through beside it, per month: the pre-discount price, or the monthly rate on a yearly plan. */
  strikePerMonthPaise: number | null;
  /** The actual charge for one unit for the selected billing period. */
  periodPaise: number | null;
  /**
   * The same struck price for the WHOLE period, in the unit `periodPaise` is in: on a yearly plan the yearly list
   * amount (twelve months at the monthly rate, or the pre-discount yearly price), so a struck figure that sits
   * beside "per year" is a yearly one. Equal to `strikePerMonthPaise` on a monthly plan.
   */
  strikePeriodPaise: number | null;
}

/**
 * The numbers a price row shows. A discount always wins the strike-through, because that is the one
 * the customer is being offered today; without one, the yearly row strikes what a year would cost at the
 * monthly rate so the saving is visible at the moment of choosing (ADR-0015 §2, M11 §13 "discounts" paragraph).
 */
export function unitPricePresentation(
  item: CatalogueItemDto,
  period: BillingPeriod,
  line?: EstimateLineDto,
): UnitPricePresentation {
  const yearly = period === "yearly";
  const divisor = yearly ? 12 : 1;
  const charged = line ? Math.round(line.unitPaise / divisor) : unitPerMonthPaise(item, period);
  const periodPaise = line?.unitPaise ?? unitPaise(item, period);
  const discounted = line?.discount != null && line.listUnitPaise != null;
  const listPeriodPaise = discounted
    ? (line?.listUnitPaise ?? 0)
    : yearly && item.price
      ? item.price.monthlyPaise * 12
      : null;
  // Struck only when it differs from the charge, compared in the period's own unit (a whole year, not a month).
  const strikePeriodPaise =
    listPeriodPaise !== null && listPeriodPaise !== periodPaise ? listPeriodPaise : null;
  return {
    perMonthPaise: charged,
    strikePerMonthPaise: strikePeriodPaise === null ? null : Math.round(strikePeriodPaise / divisor),
    periodPaise,
    strikePeriodPaise,
  };
}

/** The estimate's line for one item, if it priced one. */
export function lineFor(estimate: EstimateDto | undefined, code: string): EstimateLineDto | undefined {
  return estimate?.lines.find((l) => l.itemCode === code);
}

export interface QuantityChange {
  code: string;
  name: string;
  from: number;
  to: number;
}

export interface UsageBlocker {
  resource: "devices" | "people";
  used: number;
  allowed: number;
}

/**
 * What the api would refuse with `PLAN_DOWNGRADE_BLOCKED`, worked out before anyone presses anything:
 * a configuration smaller than what the organisation already has in use. Terminals are every terminal
 * item's quantity together (`terminalCount`, M23 — the base plan's alone would refuse 1 Access Control + 2
 * Extra terminals with 3 paired); people come off the allowance the whole configuration adds up to.
 */
export function usageBlockers(
  catalogue: readonly CatalogueItemDto[],
  config: PlanConfiguration,
  usage: { devices: number; people: number } | undefined,
): UsageBlocker[] {
  if (!usage) return [];
  const base = baseItem(catalogue);
  const devices = terminalCount(catalogue, config);
  const people = peopleAllowance(catalogue, config);
  const blockers: UsageBlocker[] = [];
  if (base && usage.devices > devices) {
    blockers.push({ resource: "devices", used: usage.devices, allowed: devices });
  }
  if (usage.people > people) blockers.push({ resource: "people", used: usage.people, allowed: people });
  return blockers;
}
